// src/services/apiService.js
import config from "../configs/config";

const REQUEST_TIMEOUT_MS = 60000; // 60s default; override per call via options.timeout
const MAX_REFRESH_RETRIES = 1; // never retry a request more than once after a refresh attempt

// Auto-logout: the session expires this long after login, regardless of activity.
export const SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 16 hours
export const LOGIN_TIME_KEY = "login_time";

const isDev = process.env.NODE_ENV !== "production";

class ApiService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
    this.token = null;
    this._refreshHandler = null;
    this._refreshInFlight = null; // dedupe concurrent refresh attempts
  }

  // ── Token lifecycle ────────────────────────────────────────────────────

  // Full login: sets the token AND resets the 16h session clock.
  // Use ONLY when the user actually (re)authenticates.
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem("auth_token", token);
      localStorage.setItem(LOGIN_TIME_KEY, Date.now().toString());
    } else {
      localStorage.removeItem("auth_token");
      localStorage.removeItem(LOGIN_TIME_KEY);
    }
  }

  // Background refresh: rotates the bearer token WITHOUT resetting the
  // session clock, so the 16h cap stays fixed relative to the original
  // login regardless of how many silent refreshes happen in between.
  updateToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem("auth_token", token);
    } else {
      localStorage.removeItem("auth_token");
    }
  }

  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem("auth_token");
    }
    return this.token;
  }

  clearAuth() {
    this.token = null;
    try {
      localStorage.clear();
    } catch (error) {
      if (isDev) console.error("Failed to clear localStorage:", error);
    }
    try {
      sessionStorage.clear();
    } catch (error) {
      if (isDev) console.error("Failed to clear sessionStorage:", error);
    }
  }

  isSessionExpired() {
    const loginTime = parseInt(localStorage.getItem(LOGIN_TIME_KEY), 10);
    if (!loginTime) return false; // no timestamp → don't force-expire
    return Date.now() - loginTime >= SESSION_MAX_AGE_MS;
  }

  // Registered by AuthProvider on mount. Must resolve to a boolean: true
  // if a fresh token was obtained, false otherwise.
  setRefreshHandler(fn) {
    this._refreshHandler = fn;
  }

  // Ensures only ONE silent refresh is ever in flight at a time, even if
  // several requests 401 at the same moment.
  async _refreshTokenOnce() {
    if (!this._refreshHandler) return false;
    if (!this._refreshInFlight) {
      this._refreshInFlight = this._refreshHandler().finally(() => {
        this._refreshInFlight = null;
      });
    }
    return this._refreshInFlight;
  }

  // ── Core request ───────────────────────────────────────────────────────

  async request(endpoint, options = {}) {
    const token = this.getToken();
    const isFormData = options.body instanceof FormData;
    const timeout = options.timeout ?? REQUEST_TIMEOUT_MS;
    const retryCount = options._retryCount ?? 0;

    const headers = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // Strip internal-only fields before they reach fetch().
    const { timeout: _t, _retryCount, ...fetchOptions } = options;

    const controller = new AbortController();
    const timerId = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : null;

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
        credentials: "include",
      });

      if (response.status === 401) {
        if (retryCount < MAX_REFRESH_RETRIES) {
          const refreshed = await this._refreshTokenOnce();
          if (refreshed) {
            return this.request(endpoint, { ...options, _retryCount: retryCount + 1 });
          }
        }
        this.clearAuth();
        // Full page navigation on purpose: guarantees all in-memory
        // app/MSAL state is wiped, not just React state.
        window.location.href = "/login";
        throw new Error("Session expired. Please log in again.");
      }

      const contentType = response.headers.get("content-type");
      const data =
        contentType && contentType.includes("application/json")
          ? await response.json()
          : await response.text();

      if (!response.ok) {
        // Only surface a short message field to the UI — never dump the
        // raw backend payload (may contain stack traces / internals).
        const message =
          (typeof data === "object" && data?.message) ||
          (typeof data === "string" && data) ||
          "Request failed";
        throw new Error(message);
      }

      return { status: response.status, data };
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("Request timed out. Please try again.");
      }
      if (isDev) console.error(`[apiService] ${endpoint} failed:`, error);
      throw error;
    } finally {
      if (timerId) clearTimeout(timerId);
    }
  }

  // ── JSON helpers ──────────────────────────────────────────────────────

  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: "GET", ...options });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
      ...options,
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
      ...options,
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: "DELETE", ...options });
  }

  // ── File / multipart upload helper ────────────────────────────────────

  async upload(endpoint, formData, options = {}) {
    if (!(formData instanceof FormData)) {
      throw new Error("upload() requires a FormData instance");
    }
    return this.request(endpoint, {
      method: "POST",
      body: formData,
      timeout: options.timeout ?? 300000,
      ...options,
    });
  }

  // ── Auth-specific convenience methods ────────────────────────────────

  async loginWithMicrosoft(idToken) {
    if (!idToken) {
      throw new Error("Missing Microsoft ID token");
    }
    try {
      const response = await this.post("/loginOffice", { id_token: idToken });
      this.setToken(idToken);
      return response;
    } catch (error) {
      if (isDev) console.error("Login API error:", error);
      throw error;
    }
  }

  async logout() {
    try {
      const token = this.getToken();
      if (token) {
        await this.post(
          "/logout",
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      if (isDev) console.warn("Logout API error:", error);
    } finally {
      this.clearAuth();
    }
  }
}

const apiService = new ApiService();
export default apiService;